import { mockItems } from '@/mocks/items';
import { systemSettings } from '@/mocks/settings';
import {
  ITEM_SERVICE_TYPES,
  type ItemDurationSettings,
  type ItemPayload,
  type ItemServiceRate,
  type ItemServiceType,
  type LaundryItem,
} from '@/types';
import { delay, shouldSimulateError } from './simulation';

/**
 * In-memory working copy so add / edit / toggle / delete behave in the preview.
 * This is a clear, replaceable demo layer — nothing is persisted to a backend.
 */
function cloneServices(
  services: Record<ItemServiceType, ItemServiceRate>,
): Record<ItemServiceType, ItemServiceRate> {
  return ITEM_SERVICE_TYPES.reduce((acc, key) => {
    acc[key] = { ...services[key] };
    return acc;
  }, {} as Record<ItemServiceType, ItemServiceRate>);
}

function cloneItem(item: LaundryItem): LaundryItem {
  return { ...item, services: cloneServices(item.services) };
}

let store: LaundryItem[] = mockItems.map(cloneItem);
let sequence = 0;

export async function fetchItems(): Promise<LaundryItem[]> {
  await delay();
  if (shouldSimulateError('items')) {
    throw new Error('failed-to-load-items');
  }
  return store.map(cloneItem);
}

/** Synchronous catalogue snapshot for pricing / review screens. */
export function getItemsSnapshot(): LaundryItem[] {
  return store.map(cloneItem);
}

export async function fetchDurationSettings(): Promise<ItemDurationSettings> {
  await delay(300);
  if (shouldSimulateError('durations')) {
    throw new Error('failed-to-load-durations');
  }
  return { ...systemSettings.durations };
}

export async function createItem(payload: ItemPayload): Promise<LaundryItem> {
  await delay();
  sequence += 1;
  const item: LaundryItem = {
    id: `ITM-${Date.now().toString().slice(-5)}${sequence}`,
    name: payload.name,
    imageUrl: payload.imageUrl,
    active: true,
    services: cloneServices(payload.services),
  };
  store = [item, ...store];
  return cloneItem(item);
}

export async function updateItem(id: string, payload: ItemPayload): Promise<LaundryItem> {
  await delay();
  store = store.map((item) =>
    item.id === id
      ? {
          ...item,
          name: payload.name,
          imageUrl: payload.imageUrl,
          services: cloneServices(payload.services),
        }
      : item,
  );
  const updated = store.find((item) => item.id === id);
  if (!updated) {
    throw new Error('item-not-found');
  }
  return cloneItem(updated);
}

export async function setItemActive(id: string, active: boolean): Promise<void> {
  await delay(400);
  store = store.map((item) => (item.id === id ? { ...item, active } : item));
}

export async function deleteItem(id: string): Promise<void> {
  await delay(400);
  store = store.filter((item) => item.id !== id);
}